import {
  AccountsReceivable,
  AccountsReceivablePayment,
  AccountsReceivableUpdate
} from '../models/AccountsReceivable';

const GATEWAY_RECEIPT_METHOD_MARKERS = [
  'asaas',
  'banco do brasil',
  'boleto (',
  'pix (',
  'cartão (',
  'cartao ('
];

function isGatewayReceiptMethod(method: string): boolean {
  const m = method.trim().toLowerCase();
  if (!m) return false;
  return GATEWAY_RECEIPT_METHOD_MARKERS.some((marker) => m.includes(marker));
}

/** Cobrança (boleto/PIX Asaas ou BB) já gerada e ainda ativa no título. */
export function hasActiveReceivableCharge(ar: AccountsReceivable | null | undefined): boolean {
  if (!ar) return false;
  const status = (ar.status || '').trim().toLowerCase();
  if (status === 'recebido' || status === 'cancelado') return false;

  if ((ar.receiptReference || '').trim()) return true;
  if ((ar.bankSlipNumber || '').trim()) return true;

  const method = (ar.receiptMethod || '').trim();
  if (method && isGatewayReceiptMethod(method)) return true;

  return false;
}

/** Indica se a conta a receber ainda permite abrir o fluxo de recebimento. */
export function canReceiveAccountsReceivable(ar: AccountsReceivable | null | undefined): boolean {
  if (!ar) return false;
  const status = (ar.status || '').trim().toLowerCase();
  if (status === 'recebido' || status === 'cancelado') return false;
  if (
    status !== 'pendente' &&
    status !== 'vencido' &&
    status !== 'aprovado' &&
    status !== 'parcialmente recebido' &&
    status !== 'parcial'
  ) {
    return false;
  }

  const toReceive = Number(ar.amountToReceive) || 0;
  const received = Number(ar.amountReceived) || 0;
  const remaining = ar.remainingAmount;
  if (remaining != null && remaining !== undefined && !isNaN(Number(remaining))) {
    if (Number(remaining) <= 0.009) return false;
  } else if (toReceive > 0 && received >= toReceive - 0.009) {
    return false;
  }

  if (hasActiveReceivableCharge(ar)) return false;

  return true;
}

/** Mensagem para tooltip quando o recebimento está bloqueado. */
export function getReceiveBlockedReason(ar: AccountsReceivable | null | undefined): string {
  if (!ar) return '';
  const status = (ar.status || '').trim().toLowerCase();
  if (status === 'recebido') return 'Título já recebido.';
  if (status === 'cancelado') return 'Título cancelado.';
  if (hasActiveReceivableCharge(ar)) {
    return 'Cobrança ou recebimento já gerado. Cancele a cobrança para gerar outro recebimento.';
  }
  const toReceive = Number(ar.amountToReceive) || 0;
  const received = Number(ar.amountReceived) || 0;
  const remaining = ar.remainingAmount;
  if (
    (remaining != null && Number(remaining) <= 0.009) ||
    (toReceive > 0 && received >= toReceive - 0.009)
  ) {
    return 'Não há saldo pendente para receber.';
  }
  return 'Recebimento indisponível para este status.';
}

/** Pagamento ainda pode ser cancelado em lote (não está Cancelado). */
export function isReceivablePaymentCancellable(payment: AccountsReceivablePayment): boolean {
  return (payment.status || '').trim().toLowerCase() !== 'cancelado';
}

/** IDs Asaas (pay_…) nas referências dos pagamentos selecionados. */
export function extractAsaasChargeIdsFromPayments(
  payments: AccountsReceivablePayment[]
): string[] {
  const ids = new Set<string>();
  for (const p of payments) {
    const ref = (p.reference || '').trim();
    if (ref.startsWith('pay_')) {
      ids.add(ref);
    }
  }
  return Array.from(ids);
}

export function extractAsaasChargeIdFromReceivable(
  ar: AccountsReceivable | null | undefined
): string | null {
  const ref = (ar?.receiptReference || '').trim();
  return ref.startsWith('pay_') ? ref : null;
}

export function formatReceivableDateForApi(date: Date | string | undefined): string {
  if (!date) return '';
  if (typeof date === 'string') {
    return date.includes('T') ? date.split('T')[0] : date;
  }
  return (date as Date).toISOString?.().split('T')[0] ?? '';
}

/** Título ainda pode ser substituído/cancelado no fluxo de acordo. */
export function canSubstituteReceivableTitleForAgreement(
  ar: AccountsReceivable | null | undefined
): boolean {
  if (!ar) return false;
  const status = (ar.status || '').trim().toLowerCase();
  return status !== 'recebido' && status !== 'cancelado';
}

/** Marca o título como cancelado ao ser substituído por um acordo. */
/** Soma N dias a uma data ISO (yyyy-MM-dd), em UTC local-safe. */
export function addDaysToIsoDate(isoDate: string, days: number): string {
  const base = new Date(`${isoDate}T12:00:00`);
  if (isNaN(base.getTime())) return isoDate;
  base.setDate(base.getDate() + days);
  return base.toISOString().split('T')[0];
}

/** 0 = domingo, 6 = sábado. */
export function isWeekendIsoDate(isoDate: string): boolean {
  const iso = String(isoDate || '').trim().slice(0, 10);
  if (!iso) return false;
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  const day = d.getDay();
  return day === 0 || day === 6;
}

/** Sábado ou domingo → próximo dia útil (segunda-feira). */
export function toNextBusinessDayIso(isoDate: string): string {
  const iso = String(isoDate || '').trim().slice(0, 10);
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  while (d.getDay() === 0 || d.getDay() === 6) {
    d.setDate(d.getDate() + 1);
  }
  return d.toISOString().split('T')[0];
}

/**
 * Divide um valor total em N parcelas (centavos distribuídos nas primeiras parcelas).
 * A soma das parcelas é sempre igual ao total informado.
 */
export function splitAmountIntoInstallments(totalAmount: number, installmentCount: number): number[] {
  const count = Math.max(1, Math.floor(installmentCount));
  const totalCents = Math.round(Math.max(0, Number(totalAmount) || 0) * 100);
  const baseCents = Math.floor(totalCents / count);
  const remainder = totalCents % count;
  const amounts: number[] = [];
  for (let i = 0; i < count; i++) {
    const cents = baseCents + (i < remainder ? 1 : 0);
    amounts.push(cents / 100);
  }
  return amounts;
}

/** Gera datas de vencimento: 1ª = firstDueDate, demais a cada intervalDays. */
export function buildInstallmentDueDates(
  firstDueDate: string,
  count: number,
  intervalDays: number
): string[] {
  const n = Math.max(1, Math.floor(count));
  const step = Math.max(1, Math.floor(intervalDays));
  const dates: string[] = [];
  for (let i = 0; i < n; i++) {
    dates.push(i === 0 ? firstDueDate : addDaysToIsoDate(firstDueDate, step * i));
  }
  return dates;
}

export function buildAgreementTitleCancelUpdate(ar: AccountsReceivable): AccountsReceivableUpdate {
  const note = '[Substituído por acordo]';
  const desc = (ar.description || '').includes(note) ? ar.description : `${ar.description || ''} ${note}`.trim();
  return {
    id: ar.id,
    documentNumber: ar.documentNumber,
    documentType: ar.documentType,
    issueDate: formatReceivableDateForApi(ar.issueDate),
    dueDate: formatReceivableDateForApi(ar.dueDate),
    originalAmount: ar.originalAmount,
    discountAmount: ar.discountAmount,
    interestAmount: ar.interestAmount,
    fineAmount: ar.fineAmount,
    amountToReceive: ar.amountToReceive,
    amountReceived: ar.amountReceived ?? 0,
    status: 'Cancelado',
    category: ar.category,
    receiptMethod: undefined,
    receiptReference: undefined,
    bankSlipNumber: undefined,
    customerId: ar.customerId,
    costCenterId: ar.costCenterId,
    issuedByStaffId: ar.issuedByStaffId,
    receivedByStaffId: ar.receivedByStaffId,
    description: desc,
    invoiceNumber: ar.invoiceNumber,
    saleOrderNumber: ar.saleOrderNumber,
    installmentNumber: ar.installmentNumber,
    totalInstallments: ar.totalInstallments,
    checkNumber: ar.checkNumber
  };
}
